/**
 * Workspace switcher — lets a platform admin move between their tenant's
 * client workspaces. The chosen workspace_id lands in localStorage under
 * `inzara.active_workspace_id`; `lib/api.js` forwards it as the
 * `X-Workspace-Id` header on every request. Backend
 * `workspace_context.resolve_workspace_id` re-validates the id server-side
 * (403 for non-admins, 404 for unknown/foreign-tenant ids).
 *
 * Non-admin sessions never see the trigger — the header would only produce
 * a 403. Sessions with a single workspace also skip the picker (no value
 * in showing a menu with one item + "Default").
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'inzara.active_workspace_id';
const DEFAULT_LABEL = 'Default workspace';

function readActive() {
  try { return window.localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
}
function writeActive(id) {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else    window.localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
}

async function fetchWorkspaces() {
  const res = await fetch(`${API}/mgmt/v1/workspaces`, { credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = await res.json();
  let rows;
  if (Array.isArray(data)) rows = data;
  else rows = data?.data || data?.items || [];
  // Auto-heal creates `ws_default_<tenant_id>` — hide that row from the
  // picker, since the "Default workspace" sentinel above already covers
  // the same context (empty X-Workspace-Id).
  return rows.filter(w => !(w?.workspace_id || '').startsWith('ws_default_'));
}

export default function WorkspaceSwitcher({ user }) {
  const role = user?.role;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState(null); // null = not loaded
  const [active, setActive] = useState(readActive());

  useEffect(() => {
    if (!isAdmin || workspaces !== null) return;
    let alive = true;
    setLoading(true);
    fetchWorkspaces()
      .then(rows => { if (alive) setWorkspaces(rows); })
      .catch(err => {
        console.debug('[workspace-switcher] list failed:', err?.message);
        if (alive) setWorkspaces([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isAdmin, workspaces]);

  const activeLabel = useMemo(() => {
    if (!active) return DEFAULT_LABEL;
    const hit = (workspaces || []).find(w => w.workspace_id === active);
    return hit?.name || active;
  }, [active, workspaces]);

  if (!isAdmin) return null;
  // Show the picker as soon as there's at least one real (non-default)
  // workspace — the menu always offers "Default workspace" as the other
  // option, so one custom workspace already means a genuine choice.
  if (workspaces && workspaces.length < 1) return null;

  function pick(workspaceId) {
    writeActive(workspaceId);
    setActive(workspaceId);
    setOpen(false);
    // Full reload so every mounted component re-fetches with the new
    // X-Workspace-Id header. Simpler and safer than surgically
    // invalidating every cache in the app.
    toast.success(
      workspaceId
        ? `Switched to ${(workspaces.find(w => w.workspace_id === workspaceId)?.name) || workspaceId}`
        : 'Switched to default workspace',
    );
    setTimeout(() => { window.location.reload(); }, 250);
  }

  return (
    <div className="relative" data-testid="workspace-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 h-8 rounded-lg border border-border/40 bg-background/40 pl-2 pr-1 text-xs text-muted-foreground hover:text-foreground hover:border-border/70 transition-colors"
        data-testid="workspace-switcher-trigger"
        title="Switch workspace"
        aria-label="Switch workspace"
      >
        <Building2 className="w-3.5 h-3.5 opacity-70" />
        <span className="max-w-[140px] truncate">{activeLabel}</span>
        <ChevronsUpDown className="w-3 h-3 opacity-50" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 top-full mt-1 z-50 w-64 rounded-lg border border-border/50 bg-popover shadow-lg py-1"
            role="menu"
            data-testid="workspace-switcher-menu"
          >
            <button
              type="button"
              onClick={() => pick('')}
              className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-accent/40"
              data-testid="workspace-switcher-item-default"
            >
              <div className="flex flex-col text-left min-w-0">
                <span className="font-medium truncate">{DEFAULT_LABEL}</span>
                <span className="text-[10px] text-muted-foreground">Your tenant&apos;s default</span>
              </div>
              {!active && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
            </button>
            <div className="my-1 h-px bg-border/40" />
            {loading && (
              <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            )}
            {!loading && (workspaces || []).map(w => (
              <button
                key={w.workspace_id}
                type="button"
                onClick={() => pick(w.workspace_id)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-accent/40"
                data-testid={`workspace-switcher-item-${w.workspace_id}`}
              >
                <div className="flex flex-col text-left min-w-0">
                  <span className="font-medium truncate">{w.name || w.workspace_id}</span>
                  <span className="text-[10px] text-muted-foreground truncate">{w.workspace_id}</span>
                </div>
                {active === w.workspace_id && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
